import React, { useState, useEffect , useRef } from 'react'
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import AddressList from './AddressList';
import { useNavigate } from 'react-router-dom';
import { useCountriesQuery, useCreateAddressMutation, useDistrictsQuery, useStatesQuery } from '../../app/config/redux/slices/api/addressApiSlice';
import AlertMessage from './AlertMessage';
import { useCancelOrderMutation, useCreateOrderMutation, useCheckDeliveryMutation } from '../../app/config/redux/slices/api/orderApiSlice';
import { clearOrder, createOrder as createOrderSlice } from '../../app/config/redux/slices/orderSlice';
import { useRazorpayPayment } from '../../app/utils/razorpayPayment';
import { applyCoupon } from '../../app/config/redux/slices/cartSlice';
import { useApplyCouponMutation } from '../../app/config/redux/slices/api/couponApiSlice';

const Checkout = () => {

    const navigate = useNavigate(); 
    const dispatch = useDispatch();
    const [processing, setProcessing] = useState(false);
    const [deliveryCharge, setDeliveryCharge] = useState(null);
    const [checkingDelivery, setCheckingDelivery] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState("payment1");
    const [selectedAddress, setSelectedAddress] = useState(null);
    const addressListRef = useRef();


    const handlePaymentChange = (event) => {
        setSelectedPayment(event.target.id);
    };


    const [createAddress] = useCreateAddressMutation()

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [mobile, setMobile] = useState('');
    const [pincode, setPincode] = useState('');
    const [house, setHouse] = useState('');
    const [area, setArea] = useState('');
    const [landmark, setLandmark] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [state, setState] = useState('');
    const [district, setDistrict] = useState('');
    const [errors, setErrors] = useState({});
    const [alert, setAlert] = useState(null);
    

    const handlePayment = useRazorpayPayment(setAlert);

    // Fetch countries
    const { data: countries, isLoading: countriesLoading, error: countriesError } = useCountriesQuery();

    // Fetch states based on selected country
    const { data: states, isLoading: statesLoading, error: statesError } = useStatesQuery(country);

    // Fetch districts based on selected state
    const { data: districts, isLoading: districtsLoading, error: districtsError } = useDistrictsQuery(state);

    const addressSubmitHandler = async (e) => {
        e.preventDefault();
        setErrors({});
        try{
            const res = await createAddress({
                data: { name: `${firstName} ${lastName}`, 
                        mobile, pincode, house, area, 
                        landmark, city, 
                        district_id : district 
                },
                token: userInfo.access_token
            });
            if (res?.error) {
                setErrors(res.error?.data?.errors ?? {}); 
            } else {
                const createdId = res.data?.data?.id;
                try {
                    addressListRef.current?.triggerRefetch(); 
                } catch (e) {
                    console.log(e);
                }
                setSelectedAddress(createdId);
                setDeliveryCharge(null);
                // Set hash
                window.location.hash = `address-${createdId}`;

                // Scroll to the address after a short delay
                setTimeout(() => {
                    const el = document.querySelector(`#address-${createdId}`);
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 500); 
                
            }
        }catch(error){
            setErrors(error?.data?.errors || {});
            console.log(error?.data?.message || "Something went wrong!");
            console.log(error);
        }
    };

    const [applyCouponApi] = useApplyCouponMutation();

    const { cartItems, coupon  } = useSelector(state => state.cart);
    const {order} = useSelector((state) => state.order);
    const {userInfo} = useSelector((state) => state.auth);

    
    const isAuthenticated = !!userInfo?.access_token;

    const subtotal = cartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);

    useEffect(() => {
        if (coupon) {
            handleApplyCoupon()
        }
    }, [coupon, subtotal, userInfo?.access_token, dispatch]);

    const [finalAmount, setFinalAmount] = useState(subtotal);
    const [discountAmount, setDiscountAmount] = useState(0);

    const handleApplyCoupon = async () => {
        const res = await applyCouponApi({ 
            data: {code: coupon, amount: subtotal }, 
            token : userInfo.access_token
        }).unwrap();

        setDiscountAmount(parseFloat(res.discount_amount));
        setFinalAmount(parseFloat(res.final_amount));
    }

    const [createOrder, { isLoading }] = useCreateOrderMutation();
    const [cancelOrder, { cancelOrderIsLoading }] = useCancelOrderMutation();
    const [checkDelivery] = useCheckDeliveryMutation();

    const orderItems = cartItems.map((item) => ({
        product_id: item.product.id.toString(),
        quantity: item.quantity.toString(),
    }));
    
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/cart');
        }
    }, [isAuthenticated, navigate]);

    const handleCancelOrder = async () => {
        try{
            const res = await cancelOrder({
                data: {
                    id: order.payment_id,
                },
                token: userInfo.access_token
            }).unwrap();
            dispatch(clearOrder());

            setAlert({ type: 'success', message: "Successfully cancelled The Order" });

            setTimeout(() => {
              setAlert(null); // Auto close after 3s
            }, 3000);

        }catch(error){

            setAlert({ type: 'danger', message: "Failed to cancel The Order" });

            setTimeout(() => {
              setAlert(null); // Auto close after 3s
            }, 3000);

        }



    } 

    useEffect(() => {
        if (selectedAddress) {
            handleCheckDelivery();
        }
    }, [selectedAddress]);

    const handleCheckDelivery = async () => {

        if (!selectedAddress) {

            setAlert({
                type: 'danger',
                message: "Please select an address first."
            });

            setTimeout(() => {
                setAlert(null);
            }, 3000);

            return;
        }

        try {

            setCheckingDelivery(true);

            const response = await checkDelivery({
                address_id: selectedAddress,
                order_items: orderItems,
                cod: 0,
                token : userInfo?.access_token
            }).unwrap();

            const totalShipping = response.vendors.reduce(
                (sum, vendor) => sum + Number(vendor.shipping_rate),
                0
            );

            setDeliveryCharge(totalShipping);

            setAlert({
                type: 'success',
                message: 'Delivery charges calculated successfully'
            });

            setTimeout(() => {
                setAlert(null);
            }, 3000);

        } catch (err) {

            console.log(err);

            setAlert({
                type: 'danger',
                message: 'Failed to calculate delivery charges'
            });

            setTimeout(() => {
                setAlert(null);
            }, 3000);

        } finally {

            setCheckingDelivery(false);

        }
    }

    const handleOrder = async () => {
        if (!selectedAddress) {
            setAlert({ type: 'danger', message: "Please select an address before placing the order." });

            setTimeout(() => {
              setAlert(null); // Auto close after 3s
            }, 3000);
            return;
        }

        if (processing) {
            setAlert({ type: 'danger', message: "Your order is already being processed. Please wait..." });

            setTimeout(() => {
              setAlert(null); // Auto close after 3s
            }, 3000);
            return;
        }; //  Prevent double clicks
        setProcessing(true);

        try {
            const response = await createOrder({
                address_id: selectedAddress,
                order_items: orderItems,
                token : userInfo?.access_token,
                 ...(coupon && { coupon_code: coupon }),
            }).unwrap();

            console.log("Order Response:", response);

            const paymentOrder = {
                order_id: response.order_id,
                payment_id: response.payment_id
            };
            
            dispatch(createOrderSlice(paymentOrder));


            handlePayment(paymentOrder);

            setAlert({ type: 'success', message: 'Order created successfully!' });

            setTimeout(() => {
            setAlert(null); // Auto close after 3s
            }, 3000);

            
            setProcessing(false);
            
        } catch (err) {
            console.error("Order Error:", err);

            // Check if err has a response object
            if (err.status) {
                if (err.data?.message) {
                    setAlert({ type: 'danger', message: err.data.message });

                    setTimeout(() => {
                        setAlert(null); // Auto close after 3s
                    }, 3000);
                } else {
                    setAlert({ type: 'danger', message: 'Failed to place order. Please try again.' });

                    setTimeout(() => {
                        setAlert(null); // Auto close after 3s
                    }, 3000);
                }
            } else {
                setAlert({ type: 'danger', message: 'Something went wrong. Please check your internet connection.' });

                setTimeout(() => {
                    setAlert(null); // Auto close after 3s
                }, 3000);
            }
            setProcessing(false);
        }
    };

    return (
        <section className="checkout py-80">
                {alert && (
                    <AlertMessage
                    message={alert.message}
                    type={alert.type}
                    onClose={() => setAlert(null)}
                    />
                )}
            <div className="container container-lg">
                <div className="border border-gray-100 rounded-8 px-30 py-20 mb-40">
                    <span className="">
                        Have a coupon?{" "}
                        <Link
                            disabled
                            to="/cart"
                            className="fw-semibold text-gray-900 hover-text-decoration-underline hover-text-main-600"
                        >
                            Click here to enter your code
                        </Link>{" "}
                    </span>
                </div>
                <div className="row">
                    <div className="col-xl-9 col-lg-8">
                        {/* AddressList */}
                        <AddressList
                            ref={addressListRef} 
                            setSelectedAddress={setSelectedAddress} 
                            selectedAddress={selectedAddress} 
                            token={userInfo?.access_token} 
                        />
                        <h5 className="mb-0">Or add a new Address</h5>
                        <br/>
                        <form onSubmit={addressSubmitHandler} className="pe-xl-5">
                        <div className="row gy-3">
                            <div className="col-sm-6 col-xs-6">
                            <input
                                type="text"
                                className="common-input border-gray-100 is-invalid"
                                placeholder="First Name"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name && errors.name[0]}</div>}
                            </div>
                            <div className="col-sm-6 col-xs-6">
                            <input
                                type="text"
                                className="common-input border-gray-100 is-invalid"
                                placeholder="Last Name"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name && errors.name[0]}</div>}
                            </div>
                            <div className="col-12">
                            <input
                                type="text"
                                className="common-input border-gray-100 is-invalid"
                                placeholder="Phone"
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                            />
                            {errors.mobile && <div className="invalid-feedback">{errors.mobile && errors.mobile[0]}</div>}
                            </div>
                            <div className="col-12">
                            <input
                                type="text"
                                className="common-input border-gray-100 is-invalid"
                                placeholder="Post Code"
                                value={pincode}
                                onChange={(e) => setPincode(e.target.value)}
                            />
                            {errors.pincode && <div className="invalid-feedback">{errors.pincode && errors.pincode[0]}</div>}
                            </div>
                            <div className="col-12">
                            <input
                                type="text"
                                className="common-input border-gray-100 is-invalid"
                                placeholder="House number and street name"
                                value={house}
                                onChange={(e) => setHouse(e.target.value)}
                            />
                            {errors.house && <div className="invalid-feedback">{errors.house && errors.house[0]}</div>}
                            </div>
                            <div className="col-12">
                            <input
                                type="text"
                                className="common-input border-gray-100 is-invalid"
                                placeholder="Area"
                                value={area}
                                onChange={(e) => setArea(e.target.value)}
                            />
                            {errors.area && <div className="invalid-feedback">{errors.area && errors.area[0]}</div>}
                            </div>
                            <div className="col-12">
                            <input
                                type="text"
                                className="common-input border-gray-100 is-invalid"
                                placeholder="Landmark"
                                value={landmark}
                                onChange={(e) => setLandmark(e.target.value)}
                            />
                            {errors.landmark && <div className="invalid-feedback">{errors.landmark && errors.landmark[0]}</div>}
                            </div>
                            <div className="col-12">
                            <input
                                type="text"
                                className="common-input border-gray-100 is-invalid"
                                placeholder="City"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                            />
                            {errors.city && <div className="invalid-feedback">{errors.city && errors.city[0]}</div>}
                            </div>
                            <div className="col-12">
                                <select
                                    className="common-input border-gray-100 is-invalid"
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                >
                                    <option value="">Select Country</option>
                                    {countriesLoading ? <option>Loading...</option> :
                                        countriesError ? <option>Error loading</option> :
                                            countries?.data?.map((country) => (
                                                <option key={country.id} value={country.id}>{country.name}</option>
                                            ))
                                    }
                                </select>
                            </div>
                            <div className="col-12">
                                <select
                                    className="common-input border-gray-100 is-invalid"
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                >
                                    <option value="">Select State</option>
                                    {statesLoading ? <option>Loading...</option> :
                                        statesError ? <option>Error loading</option> :
                                            states?.data?.map((s) => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))
                                    }
                                </select>
                            </div>
                            <div className="col-12">
                                <select
                                    className="common-input border-gray-100 is-invalid"
                                    value={district}
                                    onChange={(e) => setDistrict(e.target.value)}
                                >
                                    <option value="">Select District</option>
                                    {districtsLoading ? <option>Loading...</option> :
                                        districtsError ? <option>Error loading</option> :
                                            districts?.data?.map((d) => (
                                                <option key={d.id} value={d.id}>{d.name}</option>
                                            ))
                                    }
                                </select>
                                {errors.district_id && <div className="invalid-feedback">{errors.district_id && errors.district_id[0]}</div>}
                            </div>
                            <div className="col-12">
                            <button type="submit" className="btn btn-main mt-20 w-100">
                                Save Address
                            </button>
                            </div>
                        </div>
                        </form>
                    </div>
                    <div className="col-xl-3 col-lg-4">
                        <div className="checkout-sidebar">
                            <div className="bg-color-three rounded-8 p-24 text-center">
                                <span className="text-gray-900 text-xl fw-semibold">
                                    Your Orders
                                </span>
                            </div>
                            <div className="border border-gray-100 rounded-8 px-24 py-40 mt-24">
                                <div className="mb-32 pb-32 border-bottom border-gray-100 flex-between gap-8">
                                    <span className="text-gray-900 fw-medium text-xl font-heading-two">
                                        Product
                                    </span>
                                    <span className="text-gray-900 fw-medium text-xl font-heading-two">
                                        Subtotal
                                    </span>
                                </div>
                                {cartItems.map(item => (
                                    <div key={item.product.id} className="flex-between gap-24 mb-32">
                                        <div className="flex-align gap-12">
                                            <span className="text-gray-900 fw-normal text-md font-heading-two w-144">
                                                {item.product.name}
                                            </span>
                                            <span className="text-gray-900 fw-normal text-md font-heading-two">
                                                <i className="ph-bold ph-x" />
                                            </span>
                                            <span className="text-gray-900 fw-semibold text-md font-heading-two">
                                                {item.quantity}
                                            </span>
                                        </div>
                                        <span className="text-gray-900 fw-bold text-md font-heading-two">
                                            {'\u20B9'}{(item.product.price * item.quantity)}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-top border-gray-100 pt-30  mt-30">
                                    <div className="mb-32 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two text-xl fw-semibold">
                                            Subtotal
                                        </span>
                                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                                           {'\u20B9'}{subtotal}
                                        </span>
                                    </div>
                                    {discountAmount > 0 && (
                                        <div className="mb-32 flex-between gap-8">
                                            <span className="text-gray-900 font-heading-two">Discount</span>
                                            <span className="text-red-500 fw-semibold">- ₹ {discountAmount}</span>
                                        </div>
                                    )}
                                    {deliveryCharge !== null && (
                                        <div className="mb-32 flex-between gap-8">
                                            <span className="text-gray-900 font-heading-two">
                                                Delivery Charge
                                            </span>

                                            <span className="text-gray-900 fw-semibold">
                                               + ₹ {deliveryCharge}
                                            </span>
                                        </div>
                                    )}
                                    <div className="mb-0 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two text-xl fw-semibold">
                                            Total
                                        </span>
                                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                                            {'\u20B9'}{finalAmount + (deliveryCharge || 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-32">
                                <div className="payment-item">
                                    <div className="form-check common-check common-radio py-16 mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="payment"
                                            id="payment1"
                                            checked={selectedPayment === 'payment1'}
                                            onChange={handlePaymentChange}
                                        />
                                        <label
                                            className="form-check-label fw-semibold text-neutral-600"
                                            htmlFor="payment1"
                                        >
                                            Pay Online

                                        </label>
                                    </div>
                                    {selectedPayment === 'payment1' && (
                                        <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                                            <p className="text-gray-800">
                                                 Pay securely using UPI, Credit/Debit Cards, or Net Banking.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                {/* <div className="payment-item">
                                    <div className="form-check common-check common-radio py-16 mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="payment"
                                            id="payment2"
                                            disabled
                                            checked={selectedPayment === 'payment2'}
                                            onChange={handlePaymentChange}
                                        />
                                        <label
                                            className="form-check-label fw-semibold text-neutral-600"
                                            htmlFor="payment2"
                                        >
                                            Check payments
                                        </label>
                                    </div>
                                    {selectedPayment === 'payment2' && (
                                        <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                                            <p className="text-gray-800">
                                                Make your payment directly into our bank account. Please use your Order ID as the payment reference. Your order will not be shipped until the funds have cleared in our account.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="payment-item">
                                    <div className="form-check common-check common-radio py-16 mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="payment"
                                            id="payment3"
                                            disabled
                                            checked={selectedPayment === 'payment3'}
                                            onChange={handlePaymentChange}
                                        />
                                        <label
                                            className="form-check-label fw-semibold text-neutral-600"
                                            htmlFor="payment3"
                                        >
                                            Cash on delivery
                                        </label>
                                    </div>
                                    {selectedPayment === 'payment3' && (
                                        <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                                            <p className="text-gray-800">
                                                Make your payment directly into our bank account. Please use your Order ID as the payment reference. Your order will not be shipped until the funds have cleared in our account.
                                            </p>
                                        </div>
                                    )}
                                </div> */}
                            </div>
                            <div className="mt-32 pt-32 border-top border-gray-100">
                                <p className="text-gray-500">
                                    Your personal data will be used to process your order, support
                                    your experience throughout this website, and for other purposes
                                    described in our{" "}
                                    <Link to="/privacy-policy" className="text-main-600 text-decoration-underline">
                                        {" "}
                                        privacy policy
                                    </Link>{" "}
                                    .
                                </p>
                            </div>


                            <button
                                onClick={handleOrder}
                                disabled={isLoading || processing}
                                className="btn btn-main mt-40 py-18 w-100 rounded-8 mt-56"
                            >
                                {processing
                                    ? "Processing..."
                                    : isLoading
                                        ? "Placing Order..."
                                        : "Place Order"}
                            </button>
                            {order?.order_id ? (
                                <button
                                    onClick={handleCancelOrder}
                                    disabled={cancelOrderIsLoading}
                                    className="btn btn-danger mt-10 py-18 w-100 rounded-8"
                                >
                                    {cancelOrderIsLoading
                                        ? "Cancelling the order"
                                        : "Cancel the order"}
                                </button>
                            ) : null}

                        </div>
                    </div>
                </div>
            </div>
        </section>

    )
}

export default Checkout